"use client";

import { useId, type ReactNode } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import LinearProgress from "@mui/material/LinearProgress";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/pro-solid-svg-icons/faLock";
import { publicSans } from "@portal/app/fonts";
import { DreamLogo } from "@portal/app/_components/ui-elements/DreamLogo";
import {
  HEADING_FONT_STACK,
  SANS_FONT_STACK,
} from "@portal/app/_components/theme/options/typography";
import { isSafeFormFunnelContentHref } from "@portal/app/_common/lib/formFunnelContent";
import type {
  PayloadLegalLinksConfig,
  PopulatedPayloadFormFunnel,
} from "@portal/app/_server/services/formFunnel/payloadTypes";
import { FormFunnelFooter } from "./FormFunnelFooter";
import { FormFunnelLegalLinks } from "./FormFunnelLegalLinks";
import { FormFunnelHeadingContext } from "./FormFunnelHeadingContext";
import { getFormFunnelImageProps } from "./formFunnelMedia";
import { boundProgress } from "./utils/funnelProgress";

export interface FormFunnelLandingPageLayoutProps {
  funnel: Pick<
    PopulatedPayloadFormFunnel,
    "title" | "headerConfig" | "heroImage" | "footerConfig" | "landingPageConfig"
  >;
  legalLinks: PayloadLegalLinksConfig | null;
  progressPercent: number;
  bannerOffset?: number;
  showHeader?: boolean;
  logoClickable?: boolean;
  children: ReactNode;
}

// Match the campaign design without changing the existing funnel displays or app theme.
const landingColors = {
  blue: "#0b2ea1",
  background: "#f4f6fc",
  text: "#2b3242",
  muted: "#4b5263",
  statBackground: "#eef2fc",
  statBlue: "#2840a8",
};
const statIcons = { wage: "$", growth: "↗", openings: "+" } as const;
const wideMobile = "@media (min-width: 480px)";
const tablet = "@media (min-width: 768px)";
const desktop = "@media (min-width: 992px)";

/** A persistent landing page surrounding the active funnel screen. */
export function FormFunnelLandingPageLayout({
  funnel,
  legalLinks,
  progressPercent,
  bannerOffset = 0,
  showHeader = true,
  logoClickable = true,
  children,
}: FormFunnelLandingPageLayoutProps) {
  const formId = `funnel-form-${useId()}`;
  const titleId = `${formId}-title`;
  const statsTitleId = `${formId}-outlook`;
  const config = funnel.landingPageConfig;
  const image = getFormFunnelImageProps(funnel.heroImage);
  const stats = config?.stats ?? [];
  const sourceUrl = config?.sourceUrl?.trim();
  const hasSourceLink = isSafeFormFunnelContentHref(sourceUrl);

  return (
    <Box
      data-testid="form-funnel-page"
      data-display-option="landing"
      sx={{
        minHeight: "100vh",
        bgcolor: "common.white",
        color: landingColors.text,
        pb: 4,
        pt: `${bannerOffset}px`,
        overflowWrap: "anywhere",
      }}
    >
      {showHeader && (
        <Box
          component="header"
          data-testid="form-funnel-header"
          sx={{
            position: "sticky",
            top: bannerOffset,
            zIndex: (theme) => theme.zIndex.appBar,
            bgcolor: "common.white",
            borderBottom: "1px solid #e6e9f2",
          }}
        >
          <Container
            maxWidth={false}
            disableGutters
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
              maxWidth: "1368px",
              py: "12px",
              px: "16px",
              "& img": { display: "block" },
              [wideMobile]: { gap: "12px" },
              [tablet]: { py: "16px", px: "24px", gap: "28px" },
            }}
          >
            <DreamLogo
              width={237}
              clickable={logoClickable}
              sx={{
                flexShrink: 0,
                // DreamLogo supplies an inline wrapper height; keep its box aligned with the image.
                height: "20px !important",
                "& a": { display: "block" },
                "& img": { width: "auto", height: "20px" },
                [wideMobile]: {
                  height: "24px !important",
                  "& img": { height: "24px" },
                },
                [tablet]: {
                  height: "34px !important",
                  "& img": { height: "34px" },
                },
              }}
            />
            <Button
              href={`#${formId}`}
              variant="contained"
              sx={{
                flexShrink: 0,
                borderRadius: "999px",
                "&&&": {
                  borderRadius: "999px",
                  height: "44px",
                  px: "12px",
                  py: "8px",
                  fontSize: "13px",
                  lineHeight: "28px",
                  [wideMobile]: { px: "14px", fontSize: "14px" },
                  [tablet]: { height: "50px", px: "24px", py: "11px", fontSize: "18px" },
                },
                fontWeight: 600,
                bgcolor: landingColors.blue,
                "&:hover": { bgcolor: landingColors.blue },
                textTransform: "none",
                boxShadow: "none",
              }}
            >
              Free Matching
            </Button>
          </Container>
          <LinearProgress
            data-testid="funnel-progress-bar"
            aria-label="Matching progress"
            variant="determinate"
            value={boundProgress(progressPercent)}
            sx={{
              position: "absolute",
              bottom: 0,
              width: "100%",
              height: 2,
              bgcolor: "#e6e9f2",
              "& .MuiLinearProgress-bar": { bgcolor: landingColors.blue },
            }}
          />
        </Box>
      )}
      <Box sx={{ bgcolor: landingColors.background }}>
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            maxWidth: "1368px",
            p: "20px",
            [tablet]: { p: "24px" },
          }}
        >
          <Box
            data-testid="form-funnel-body"
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr)",
              gridTemplateAreas: image
                ? stats.length
                  ? '"form" "image" "stats"'
                  : '"form" "image"'
                : stats.length
                  ? '"form" "stats"'
                  : '"form"',
              [tablet]: {
                rowGap: "28px",
                gridTemplateAreas: image
                  ? stats.length
                    ? '"image" "form" "stats"'
                    : '"image" "form"'
                  : stats.length
                    ? '"form" "stats"'
                    : '"form"',
              },
              [desktop]: {
                gridTemplateColumns: image ? "500px minmax(0, 1fr)" : "minmax(0, 1fr)",
                gridTemplateAreas: image
                  ? stats.length
                    ? '"image form" "image stats"'
                    : '"image form"'
                  : stats.length
                    ? '"form" "stats"'
                    : '"form"',
              },
              alignItems: "start",
              columnGap: "48px",
              rowGap: "20px",
            }}
          >
            <Box sx={{ gridArea: "form", minWidth: 0, pt: "4px" }}>
              {config?.eyebrow?.trim() && (
                <Typography
                  sx={{
                    color: landingColors.blue,
                    fontSize: "14px",
                    lineHeight: "28px",
                    fontWeight: 600,
                    letterSpacing: "1.6px",
                    textTransform: "uppercase",
                    mb: "8px",
                    [tablet]: { fontSize: "15px", letterSpacing: "1.4px", mb: "12px" },
                  }}
                >
                  {config.eyebrow}
                </Typography>
              )}
              <Typography
                id={titleId}
                component="h1"
                sx={{
                  fontFamily: HEADING_FONT_STACK,
                  fontSize: "44px",
                  lineHeight: 1.05,
                  [tablet]: { fontSize: "48px", lineHeight: 1.02 },
                  [desktop]: { fontSize: "62px" },
                  fontWeight: 700,
                  letterSpacing: "normal",
                  color: landingColors.blue,
                  mb: 0,
                }}
              >
                {funnel.headerConfig?.title || funnel.title}
              </Typography>
              {funnel.headerConfig?.subtitle?.trim() && (
                <Typography
                  sx={{
                    maxWidth: "720px",
                    fontFamily: publicSans.style.fontFamily,
                    color: landingColors.text,
                    fontSize: "18px",
                    lineHeight: 1.5,
                    mt: "10px",
                    [tablet]: { fontSize: "20px", mt: "14px" },
                  }}
                >
                  {funnel.headerConfig.subtitle}
                </Typography>
              )}
              <Box
                id={formId}
                component="section"
                aria-label="School matching form"
                tabIndex={-1}
                data-testid="form-funnel-inline-form"
                sx={{
                  minWidth: 0,
                  scrollMarginTop: `${bannerOffset + (showHeader ? 104 : 16)}px`,
                  mt: "22px",
                  [tablet]: { mt: "24px" },
                  // The form belongs to the hero; avoid a second padded card within it.
                  "& h2, & h3": { fontFamily: SANS_FONT_STACK, fontWeight: 600 },
                  "& > *": { minWidth: 0 },
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: 4,
                  },
                }}
              >
                <FormFunnelHeadingContext.Provider value="h2">
                  {children}
                </FormFunnelHeadingContext.Provider>
              </Box>
              {config?.trustText?.trim() && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: "6px",
                    mt: "14px",
                    justifyContent: "center",
                    textAlign: "center",
                    [tablet]: { mt: "20px", justifyContent: "flex-start", textAlign: "left" },
                    color: landingColors.text,
                  }}
                >
                  <FontAwesomeIcon icon={faLock} aria-hidden="true" />
                  <Typography
                    sx={{ color: landingColors.text, fontSize: "15px", lineHeight: "28px" }}
                  >
                    {config.trustText}
                  </Typography>
                </Box>
              )}
            </Box>
            {image && (
              <Box
                data-testid="form-funnel-hero"
                sx={{
                  gridArea: "image",
                  minWidth: 0,
                  position: "relative",
                  overflow: "hidden",
                  alignSelf: "stretch",
                  borderRadius: "16px",
                  height: 190,
                  "& img": { objectPosition: "50% 15%" },
                  [tablet]: {
                    height: 420,
                    borderRadius: "22px",
                    "& img": { objectPosition: "50% 20%" },
                  },
                  [desktop]: { height: "auto", minHeight: 712 },
                  bgcolor: landingColors.statBackground,
                }}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  width={image.width}
                  height={image.height}
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "block",
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
                {config?.imageLabel?.trim() && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "18px",
                      left: "18px",
                      right: "18px",
                      display: "none",
                      [tablet]: { display: "flex" },
                      justifyContent: "flex-start",
                    }}
                  >
                    <Typography
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "9px",
                        borderRadius: "999px",
                        bgcolor: "common.white",
                        color: "#0b2370",
                        p: "9px 16px 9px 14px",
                        fontWeight: 600,
                        fontSize: "15px",
                        lineHeight: "28px",
                      }}
                    >
                      <Box
                        component="span"
                        aria-hidden
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: "#0b2370",
                          flexShrink: 0,
                        }}
                      />
                      {config.imageLabel}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            {stats.length > 0 && (
              <Box
                component="section"
                aria-labelledby={statsTitleId}
                data-testid="form-funnel-career-outlook"
                sx={{
                  gridArea: "stats",
                  minWidth: 0,
                  p: "22px 20px 18px",
                  borderRadius: "18px",
                  [tablet]: { p: "24px 28px 20px", borderRadius: "20px" },
                  bgcolor: "common.white",
                }}
              >
                {config?.statsEyebrow?.trim() && (
                  <Typography
                    sx={{
                      fontSize: "11.5px",
                      lineHeight: "28px",
                      fontWeight: 400,
                      letterSpacing: "1.3px",
                      [tablet]: { fontSize: "13px", letterSpacing: "2px" },
                      textTransform: "uppercase",
                      mb: "6px",
                      color: "#3b4252",
                    }}
                  >
                    {config.statsEyebrow}
                  </Typography>
                )}
                <Typography
                  id={statsTitleId}
                  component="h2"
                  sx={{
                    fontFamily: SANS_FONT_STACK,
                    fontSize: "22px",
                    color: "#0f1a33",
                    fontWeight: 600,
                    lineHeight: 1.25,
                    mb: "14px",
                    [tablet]: { fontSize: "26px", mb: "16px" },
                  }}
                >
                  {config?.statsTitle?.trim() || "Career outlook"}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    [tablet]: { gap: "14px" },
                    m: 0,
                  }}
                >
                  {stats.map((stat, index) => (
                    <Box
                      key={stat.id ?? index}
                      sx={{
                        display: "flex",
                        flex: "1 1 min-content",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                        minWidth: 0,
                        gap: "8px",
                        p: "12px 8px 14px 10px",
                        [tablet]: {
                          p: "16px",
                          flexDirection: "row",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: "16px",
                          borderRadius: "16px",
                        },
                        borderRadius: "14px",
                        bgcolor: landingColors.statBackground,
                      }}
                    >
                      {stat.icon && (
                        <Box
                          component="span"
                          aria-hidden
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            width: 34,
                            height: 34,
                            borderRadius: "8px",
                            bgcolor: landingColors.statBlue,
                            color: "primary.contrastText",
                            fontSize: "17px",
                            fontWeight: 600,
                            [tablet]: {
                              width: 48,
                              height: 48,
                              borderRadius: "11px",
                              fontSize: "22px",
                            },
                          }}
                        >
                          {statIcons[stat.icon]}
                        </Box>
                      )}
                      <Box
                        component="dl"
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          flex: "1 1 min-content",
                          minWidth: 0,
                          width: "100%",
                          m: 0,
                          [tablet]: {
                            width: "auto",
                          },
                        }}
                      >
                        <Typography
                          component="dt"
                          sx={{
                            order: 1,
                            color: landingColors.text,
                            fontSize: "14px",
                            lineHeight: 1.3,
                            [tablet]: { lineHeight: 1.4 },
                            mt: "3px",
                          }}
                        >
                          {stat.label}
                        </Typography>
                        <Typography
                          component="dd"
                          sx={{
                            m: 0,
                            color: landingColors.statBlue,
                            fontSize: "21px",
                            letterSpacing: "-0.3px",
                            overflowWrap: "break-word",
                            [wideMobile]: { fontSize: "22px" },
                            [tablet]: {
                              fontSize: "32px",
                              letterSpacing: 0,
                            },
                            fontWeight: 600,
                            lineHeight: 1.1,
                          }}
                        >
                          {stat.value}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
                {(config?.sourceText?.trim() || hasSourceLink) && (
                  <Typography
                    sx={{
                      mt: "14px",
                      fontSize: "13.5px",
                      fontFamily: publicSans.style.fontFamily,
                      lineHeight: 1.55,
                      color: landingColors.muted,
                      [tablet]: { mt: "16px", fontSize: "13px" },
                    }}
                  >
                    {config?.sourceText}
                    {hasSourceLink && (
                      <>
                        {config?.sourceText?.trim() && " "}
                        <Link
                          href={sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ color: "#2050c8" }}
                        >
                          {config?.sourceLinkLabel?.trim() || "View source"}
                        </Link>
                      </>
                    )}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Container>
      </Box>
      {funnel.footerConfig && <FormFunnelFooter footerConfig={funnel.footerConfig} />}
      {legalLinks && <FormFunnelLegalLinks config={legalLinks} />}
    </Box>
  );
}
